import { RSAA } from 'redux-api-middleware';
import _ from 'lodash';

import { store } from 'src/core';
import { getToken } from 'src/auth';

import { api } from '../config';

export const Status = Object.freeze({
  CLEAR: '@@status.clear',
  LOADING: '@@status.loading',
  VALID: '@@status.valid',
  ERROR: '@@status.ERROR',
});

export const INITIAL_API_STATE = {
  status: {
    state: Status.CLEAR,
  },
  data: {},
};

export const INITIAL_API_STATE_ITEMS = {
  status: {
    state: Status.CLEAR,
  },
  data: {
    items: [],
  },
};

export const INITIAL_PAGING = {
  page: 1,
  pageSize: 10,
  totalResults: 0,
  totalPages: 1,
};

/**
 * Function which creates RSAA action for given api config and associated action types
 * @param {string|Object} apiConfig if string it is interprated as path in api endpoint
 * @param {string} apiConfig.hostname hostname of the api, defaults to:
 * 'https://staging2-ro.mytime.com/api/mkp/v1' saved in app/api/config.js
 * @param {string} apiConfig.path path on the hostname which combined give an api endpoint
 * @param {string} apiConfig.method HTTP request method, defaults to 'GET'
 * @param {Object} apiConfig.headers headers to be used in HTTP request
 */
export const createApiAction = (apiConfig, actionTypes) => {
  const hostname = apiConfig.hostname || api.hostname;
  const path = _.isString(apiConfig) ? apiConfig : apiConfig.path;
  const method = _.get(apiConfig, 'method', 'GET');
  const contentType =
    _.includes(['POST', 'PUT', 'PATCH'], method) && !apiConfig.formData
      ? {
          'Content-Type': 'application/json',
        }
      : {};

  const passedHeaders = _.get(apiConfig, 'headers', {});
  const headers = {
    ...contentType,
    ...passedHeaders,
  };
  return {
    [RSAA]: {
      endpoint: `${hostname}${path}`,
      headers,
      method,
      types: actionTypes,
      body: apiConfig.formData || JSON.stringify(apiConfig.body),
    },
  };
};

/**
 * Use this function to create any reducer which handles api communication
 * @param {Array} actionTypes Array of action types associated with fetchThunk e.g. USER_REQUEST,
 * USER_SUCCESS and USER_ERROR
 * @param {function} dataAdapter function which will be called to transform response body
 * to format you want to use in state
 * @param {function} errorAdapter function which will be called to transform error
 * to format you want to use in state
 * @returns {function} reducer handles api requests, saves response body as data and status
 */

// eslint-disable-next-line max-len
export const createApiReducer = (
  actionTypes,
  dataAdapter = data => data,
  errorAdapter = action =>
    _.get(
      action,
      'payload.error.message',
      _.get(action, 'payload.error', 'Undefined error')
    ),
  clearOnSignOut = false
) => (state = INITIAL_API_STATE, action) => {
  const [REQUEST, SUCCESS, ERROR] = actionTypes;

  if (clearOnSignOut && action.type === 'auth/SIGN_OUT') {
    return INITIAL_API_STATE;
  }
  switch (action.type) {
    case REQUEST:
      return {
        ...state,
        status: {
          state: Status.LOADING,
          dateTime: Date.now(),
          endpoint: action.endpoint,
        },
      };
    case SUCCESS:
      return {
        status: {
          state: Status.VALID,
          dateTime: Date.now(),
        },
        data: dataAdapter(action.payload, state),
      };
    case ERROR:
      return {
        ...state,
        status: {
          state: Status.ERROR,
          error: action.error,
          errorMessage: errorAdapter(action),
        },
      };

    default:
      return state;
  }
};

/**
 * use this to create smart state objects. e.g. to create user selector
 * call this:
 * const getUser = state => addTraits(state.user);
 * and then in mapStateToProps:
 * user: getUser(state),
 * This user is going to have some traits so you can check is it currently loading:
 * user.isLoading();
 */
export function addTraits({ data = {}, status } = {}) {
  return {
    ...data,
    isValid() {
      return status.state === Status.VALID;
    },

    isLoading() {
      return status.state === Status.LOADING;
    },

    isError() {
      return status.state === Status.ERROR;
    },

    errorMessage() {
      return _.get(status, 'errorMessage.message', status.errorMessage);
    },

    errorCode() {
      return _.get(status, 'errorMessage.code');
    },

    shouldLoad() {
      return status.state === Status.CLEAR || status.state === Status.ERROR;
    },

    isEmpty() {
      return _.isEmpty(data);
    },

    paging() {
      return data._paging || INITIAL_PAGING;
    },
  };
}

/**
 * This is higher order reducer
 * Use this if you want your state is combination of results of multiple reducers
 * under the same key, e.g. you can get the same data from multiple endpoints
 */
export const chainReducers = (initialState, ...args) => (
  state = initialState,
  action
) => args.reduce((newState, reducer) => reducer(newState, action), state);

/**
 * API call function that will send the token from redux store in header
 */
export const apiRequest = async ({
  url,
  method = 'GET',
  data,
  withAuthentication = true,
  responseDataAdapter = data => data,
}) => {
  const { accessToken } = getToken(store.getState());
  const contentType = data ? { 'Content-Type': 'application/json' } : {};
  const headers = {
    ...contentType,
  };

  if (withAuthentication && accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  try {
    const response = await fetch(`${api.hostname}${url || ''}`, {
      method,
      headers,
      body: JSON.stringify(data),
    });

    const responseData = await response.json();

    if (response.ok) {
      return { ok: true, data: responseDataAdapter(responseData) };
    }

    return {
      ok: false,
      error: {
        status: response.status,
        statusText: response.statusText,
        body: responseData,
      },
    };
  } catch (e) {
    return {
      ok: false,
      error: {
        statusText: 'internal error',
        body: e,
      },
    };
  }
};
